import React, { useState, useEffect, useCallback, useRef } from 'react';
import { host } from '../script/variables';
import '../style/RecipeManager.css';

/* ═══════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════ */
const UNITS      = ['g', 'kg', 'ml', 'liter', 'pcs'];
const MEAL_ICONS = { breakfast: '🌅', lunch: '☀️', dinner: '🌙', snacks: '🍿', dessert: '🍮', water: '💧' };
const TYPE_COLORS = { veg: '#16a34a', nonveg: '#dc2626', omnivores: '#d97706' };

const CAT_ICONS = {
  vegetables:            '🥦',
  dairy:                 '🥛',
  grains_and_cereals:    '🌾',
  spices_and_herbs:      '🌿',
  oils_and_fats:         '🫙',
  meat_and_poultry:      '🍗',
  seafood:               '🐟',
  beverages:             '🧃',
  fruits:                '🍎',
  bakery:                '🍞',
  condiments_and_sauces: '🧴',
  dry_goods:             '🥫',
  other:                 '📦',
};

const CAT_LABELS = {
  vegetables:            'Vegetables',
  dairy:                 'Dairy Products',
  grains_and_cereals:    'Grains & Cereals',
  spices_and_herbs:      'Spices & Herbs',
  oils_and_fats:         'Oils & Fats',
  meat_and_poultry:      'Meat & Poultry',
  seafood:               'Seafood',
  beverages:             'Beverages',
  fruits:                'Fruits',
  bakery:                'Bakery & Baking',
  condiments_and_sauces: 'Condiments & Sauces',
  dry_goods:             'Dry Goods',
  other:                 'Other',
};

const getToken   = () => localStorage.getItem('partnertoken');
const fmtQty     = (n) => (n % 1 === 0 ? n : parseFloat(n.toFixed(4)));
const newIngRow  = () => ({
  _tempId:   Math.random().toString(36).slice(2),
  rawItemId: '',
  label:     '',
  quantity:  '',
  unit:      'g',
  fromInv:   false,
  invItem:   null,
});

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
const groupByCategory = (items) => {
  const map = {};
  for (const item of items) {
    const cat = item.category || 'other';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  }
  return map;
};

/** Total usable quantity expressed in baseUnit */
const totalStock = (item) => item.quantity * item.baseQuantityPerUnit;

/* ── FIX: Unit conversion ─────────────────────────────────
   The original code passed r.quantity (in r.unit) directly
   to estimateCost / stockOk which both expect a value in
   invItem.baseUnit.  This caused wrong cost estimates and
   incorrect stock-warning logic whenever the recipe unit
   differed from the inventory baseUnit.

   Strategy (in priority order):
   1. fromUnit === baseUnit → no conversion needed
   2. fromUnit === invItem.unit (purchase unit) → multiply
      by baseQuantityPerUnit (most reliable, uses the exact
      ratio stored in inventory)
   3. Standard metric pairs (kg↔g, liter/l↔ml) as fallback
      for cases where inventory is missing or mismatched
   ─────────────────────────────────────────────────────── */
const toBaseUnit = (qty, fromUnit, invItem) => {
  if (!qty || !fromUnit || !invItem) return Number(qty);
  const q    = Number(qty);
  const from = fromUnit.toLowerCase();
  const to   = (invItem.baseUnit || '').toLowerCase();

  if (from === to) return q;                                        // 1. already in base
  if (from === (invItem.unit || '').toLowerCase())
    return q * invItem.baseQuantityPerUnit;                         // 2. purchase unit → base

  // 3. Standard metric fallbacks
  if (from === 'kg'    && to === 'g')     return q * 1000;
  if (from === 'g'     && to === 'kg')    return q / 1000;
  if ((from === 'liter' || from === 'l') && to === 'ml') return q * 1000;
  if (from === 'ml' && (to === 'liter' || to === 'l'))   return q / 1000;

  return q; // unknown pair — pass through unchanged
};

/** True if current stock covers needed qty (both in baseUnit after conversion) */
const stockOk = (invItem, qty, unit) => {
  if (!invItem || !qty) return true;
  return totalStock(invItem) >= toBaseUnit(qty, unit, invItem);
};

/** Cost estimate in ₹ for qty (in recipe unit) of an inventory item */
const estimateCost = (invItem, qty, unit) => {
  if (!invItem || !qty || !invItem.unitPrice) return null;
  const qtyBase = toBaseUnit(qty, unit, invItem);            // convert to baseUnit
  const cost    = (qtyBase / invItem.baseQuantityPerUnit) * invItem.unitPrice;
  return isNaN(cost) ? null : cost.toFixed(2);
};

/* ═══════════════════════════════════════════════════════════
   SMALL SUBCOMPONENTS
═══════════════════════════════════════════════════════════ */
const Ring = ({ pct, size = 52, stroke = 5 }) => {
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e8e3d8" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#c8a45a" strokeWidth={stroke}
        strokeDasharray={`${circ * pct / 100} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray .6s ease' }} />
    </svg>
  );
};

const Pill = ({ label, color }) => (
  <span className="rm-pill" style={{ background: color + '18', color, borderColor: color + '40' }}>{label}</span>
);

const Toast = ({ msg, type, onDone }) => {
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, [onDone]);
  return <div className={`rm-toast rm-toast--${type}`}>{type === 'ok' ? '✓' : '✕'} {msg}</div>;
};

const UnitToggle = ({ value, onChange }) => (
  <div className="rm-unit-toggle">
    {UNITS.map(u => (
      <button key={u} type="button"
        className={`rm-unit-btn${value === u ? ' active' : ''}`}
        onClick={() => onChange(u)}>{u}</button>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   INGREDIENT COMBOBOX
═══════════════════════════════════════════════════════════ */
const IngredientCombobox = ({ row, inventory, onSelect, onCustom }) => {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState(row.label || '');
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { setQuery(row.label || ''); }, [row.label]);

  const filtered = query.trim()
    ? inventory.filter(i => i.name.toLowerCase().includes(query.toLowerCase()))
    : inventory;

  const grouped = groupByCategory(filtered);
  const cats    = Object.keys(grouped).sort();

  const pick = (item) => { setQuery(item.name); setOpen(false); onSelect(item); };

  const onBlur = () => {
    setTimeout(() => {
      setOpen(false);
      const exact = inventory.find(i => i.name.toLowerCase() === query.trim().toLowerCase());
      if (exact) onSelect(exact);
      else if (query.trim()) onCustom(query.trim());
    }, 160);
  };

  return (
    <div className="rm-combobox-wrap" ref={wrapRef}>
      <div className="rm-combobox-input-row">
        <input
          className={`rm-input rm-ing-name${row.fromInv ? ' rm-input--linked' : ''}`}
          placeholder="Search inventory or type custom…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); onCustom(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          autoComplete="off"
        />
        {row.fromInv && <span className="rm-inv-linked-badge" title="Linked to inventory">🔗</span>}
      </div>

      {open && (
        <div className="rm-combobox-dropdown">
          {inventory.length === 0 && (
            <p className="rm-combobox-empty">No inventory items. Add stock first.</p>
          )}
          {query.trim() && filtered.length === 0 && (
            <div className="rm-combobox-custom-row" onMouseDown={() => { onCustom(query.trim()); setOpen(false); }}>
              <span className="rm-combobox-custom-icon">✎</span>
              <span>Use "<strong>{query}</strong>" as custom ingredient</span>
            </div>
          )}
          {cats.map(cat => (
            <div key={cat} className="rm-combobox-group">
              <div className="rm-combobox-group-label">
                {CAT_ICONS[cat]} {CAT_LABELS[cat] || cat}
              </div>
              {grouped[cat].map(item => {
                const low   = item.lowStockThreshold > 0 && item.quantity <= item.lowStockThreshold;
                const stock = fmtQty(totalStock(item));
                return (
                  <div key={item._id}
                    className={`rm-combobox-item${low ? ' rm-combobox-item--low' : ''}`}
                    onMouseDown={() => pick(item)}>
                    <div className="rm-combobox-item-left">
                      <span className="rm-combobox-item-name">{item.name}</span>
                      {low && <span className="rm-low-badge">Low stock</span>}
                    </div>
                    <div className="rm-combobox-item-right">
                      <span className="rm-combobox-stock">{stock}{item.baseUnit}</span>
                      <span className="rm-combobox-unit-pill">{item.unit}</span>
                      {item.unitPrice > 0 && (
                        <span className="rm-combobox-price">₹{item.unitPrice}/{item.unit}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RECIPE EDITOR
═══════════════════════════════════════════════════════════ */
const RecipeEditor = ({ dish, mealType, categoryName, cafeid, inventory, onSaved, onClose }) => {
  const [ingredients, setIngredients] = useState([]);
  const [bulkMode,    setBulkMode]    = useState(false);
  const [bulkQty,     setBulkQty]     = useState(10);
  const [isTaxable,   setIsTaxable]   = useState(dish.isTaxable ?? false);
  const [saving,      setSaving]      = useState(false);
  const [dirty,       setDirty]       = useState(false);

  useEffect(() => {
    const rows = (dish.recipe || []).map(r => {
      const inv = inventory.find(i => i._id === r.rawItemId || i.name === r.rawItemId);
      return {
        _tempId:   Math.random().toString(36).slice(2),
        rawItemId: r.rawItemId || '',
        label:     inv ? inv.name : (r.rawItemId || ''),
        quantity:  r.quantity || '',
        unit:      r.unit || (inv ? inv.baseUnit : 'g'),
        fromInv:   !!inv,
        invItem:   inv || null,
      };
    });
    setIngredients(rows.length ? rows : [newIngRow()]);
  }, [dish, inventory]); // eslint-disable-line

  const mark = () => setDirty(true);

  const add    = ()         => { setIngredients(p => [...p, newIngRow()]); mark(); };
  const remove = (id)       => { setIngredients(p => p.filter(r => r._tempId !== id)); mark(); };
  const upd    = (id, k, v) => { setIngredients(p => p.map(r => r._tempId === id ? { ...r, [k]: v } : r)); mark(); };

  const handleInvSelect = (tempId, item) => {
    setIngredients(p => p.map(r => r._tempId !== tempId ? r : {
      ...r, rawItemId: item._id, label: item.name,
      unit: item.baseUnit, fromInv: true, invItem: item,
    }));
    mark();
  };

  const handleCustom = (tempId, text) => {
    setIngredients(p => p.map(r => r._tempId !== tempId ? r : {
      ...r, rawItemId: text, label: text, fromInv: false, invItem: null,
    }));
    mark();
  };

  const resolved = () => ingredients.map(r => ({
    rawItemId: r.rawItemId.trim() || r.label.trim(),
    quantity:  bulkMode ? fmtQty(Number(r.quantity) / bulkQty) : fmtQty(Number(r.quantity)),
    unit:      r.unit,
  }));

  const handleSave = async () => {
    const rows = resolved();
    for (const r of rows) {
      if (!r.rawItemId)      return alert('Every ingredient needs a name.');
      if (!(r.quantity > 0)) return alert(`"${r.rawItemId}" needs a positive quantity.`);
    }
    setSaving(true);
    try {
      const res = await fetch(`${host}/recipe/saveRecipe`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body:    JSON.stringify({ cafeid, dishId: dish._id, mealType, categoryName, dishName: dish.name, recipe: rows, isTaxable }),
      });
      const j = await res.json();
      if (j.success) { setDirty(false); onSaved('Recipe saved!'); }
      else alert(j.msg || 'Save failed.');
    } catch { alert('Network error.'); }
    finally { setSaving(false); }
  };

  /* ── Per-row display helpers — all now pass r.unit to cost/stock fns ── */

  const perItemHint = (r) => {
    if (!bulkMode || !r.quantity) return null;
    return (
      <span className="rm-per-item-hint">
        = {fmtQty(Number(r.quantity) / bulkQty)}{r.unit}/item
      </span>
    );
  };

  /* FIX: stockOk now receives the recipe unit so it can convert before comparing */
  const stockWarn = (r) => {
    if (!r.invItem || !r.quantity) return null;
    const qty = bulkMode ? Number(r.quantity) / bulkQty : Number(r.quantity);
    if (stockOk(r.invItem, qty, r.unit)) return null;
    const avail = fmtQty(totalStock(r.invItem));
    return (
      <span className="rm-stock-warn">
        ⚠ Only {avail}{r.invItem.baseUnit} in stock
      </span>
    );
  };

  /* FIX: estimateCost now receives the recipe unit for correct conversion */
  const rowCost = (r) => {
    if (!r.invItem || !r.quantity) return null;
    const qty  = bulkMode ? Number(r.quantity) / bulkQty : Number(r.quantity);
    const cost = estimateCost(r.invItem, qty, r.unit);
    return cost ? <span className="rm-cost-inline">≈ ₹{cost}/item</span> : null;
  };

  /* FIX: totalCost now converts each ingredient's qty to baseUnit first */
  const totalCost = ingredients.reduce((s, r) => {
    if (!r.invItem || !r.quantity) return s;
    const qty  = bulkMode ? Number(r.quantity) / bulkQty : Number(r.quantity);
    const c    = estimateCost(r.invItem, qty, r.unit);
    return c ? s + parseFloat(c) : s;
  }, 0);

  /* FIX: Selling price for margin = dish price ÷ 1.05 when taxable
     (the menu price includes GST, so actual revenue is price/1.05) */
  const sellingPrice  = dish.medium?.price ? Number(dish.medium.price) : 0;
  const revenuePerItem = isTaxable ? sellingPrice / 1.05 : sellingPrice;
  const grossMarginPct = sellingPrice > 0 && totalCost > 0
    ? Math.max(0, ((revenuePerItem - totalCost) / revenuePerItem) * 100).toFixed(0)
    : null;

  /* Unit mismatch hint — shown when recipe unit ≠ invItem baseUnit and
     no known conversion path exists */
  const unitMismatchWarn = (r) => {
    if (!r.invItem || !r.unit) return null;
    const from = r.unit.toLowerCase();
    const to   = (r.invItem.baseUnit || '').toLowerCase();
    if (from === to) return null;
    if (from === (r.invItem.unit || '').toLowerCase()) return null;
    const knownPairs = [['kg','g'],['g','kg'],['liter','ml'],['l','ml'],['ml','liter'],['ml','l']];
    if (knownPairs.some(([a,b]) => from===a && to===b)) return null;
    
    return (

      <span style={{'color':'red'}} className="rm-unit-warn" title={`Inventory uses ${r.invItem.baseUnit}, recipe uses ${r.unit}. Cost may be approximate.`}>
        ⚠ unit mismatch
      </span>
    );
  };

  const linkedCount = ingredients.filter(r => r.fromInv).length;
  const customCount = ingredients.filter(r => !r.fromInv && r.label).length;
  const warnCount   = ingredients.filter(r => {
    if (!r.invItem || !r.quantity) return false;
    const qty = bulkMode ? Number(r.quantity) / bulkQty : Number(r.quantity);
    return !stockOk(r.invItem, qty, r.unit);
  }).length;

  return (
    <div className="rm-editor">

      {/* ── Header ── */}
      <div className="rm-editor-head">
        <div className="rm-editor-title-row">
          <span className="rm-dish-dot" style={{ background: TYPE_COLORS[dish.type] }} />
          <h3 className="rm-editor-title">{dish.name}</h3>
          <Pill label={dish.type} color={TYPE_COLORS[dish.type]} />
          {dish.medium?.price && <span className="rm-price-tag">₹{dish.medium.price}</span>}
        </div>
        <div className="rm-editor-meta">
          <span className="rm-editor-breadcrumb">{mealType} › {categoryName}</span>
          <label className="rm-taxable-toggle">
            <input type="checkbox" checked={isTaxable} onChange={e => { setIsTaxable(e.target.checked); mark(); }} />
            <span className="rm-toggle-track" />
            <span>Taxable (5% GST)</span>
          </label>
        </div>
      </div>

      {/* ── Inventory context strip ── */}
      <div className="rm-inv-strip">
        <div className="rm-inv-strip-left">
          <span>📦</span>
          <span>{inventory.length} inventory items available</span>
        </div>
        <div className="rm-inv-strip-chips">
          {linkedCount > 0 && <span className="rm-strip-chip rm-strip-chip--linked">🔗 {linkedCount} linked</span>}
          {customCount > 0 && <span className="rm-strip-chip rm-strip-chip--custom">✎ {customCount} custom</span>}
          {warnCount   > 0 && <span className="rm-strip-chip rm-strip-chip--warn">⚠ {warnCount} low stock</span>}
        </div>
      </div>

      {/* ── Bulk mode bar ── */}
      <div className="rm-bulk-bar">
        <div className="rm-bulk-bar-left">
          <button className={`rm-mode-btn${!bulkMode ? ' active' : ''}`}
            onClick={() => { setBulkMode(false); mark(); }}>Per Item</button>
          <button className={`rm-mode-btn${bulkMode ? ' active' : ''}`}
            onClick={() => { setBulkMode(true); mark(); }}>Bulk ÷ Mode</button>
        </div>
        {bulkMode ? (
          <div className="rm-bulk-qty-row">
            <span className="rm-bulk-label">Recipe is for</span>
            <input type="number" min={1} max={9999} className="rm-bulk-input" value={bulkQty}
              onChange={e => setBulkQty(Math.max(1, Number(e.target.value)))} />
            <span className="rm-bulk-label">items — system divides to 1</span>
          </div>
        ) : (
          <p className="rm-bulk-hint">
            Switch to <strong>Bulk ÷ Mode</strong> to enter batch quantities — we'll divide to per-item automatically.
          </p>
        )}
      </div>

      {/* ── Ingredient rows ── */}
      <div className="rm-ingredients">
        <div className="rm-ing-head">
          <span>Ingredient</span>
          <span>Qty {bulkMode && `(for ${bulkQty})`}</span>
          <span>Unit</span>
          <span />
        </div>

        {ingredients.map(r => (
          <div className="rm-ing-row" key={r._tempId}>

            <div className="rm-ing-name-wrap">
              <IngredientCombobox
                row={r}
                inventory={inventory}
                onSelect={(item) => handleInvSelect(r._tempId, item)}
                onCustom={(text) => handleCustom(r._tempId, text)}
              />
              {r.fromInv && r.invItem && (
                <div className="rm-inv-meta-row">
                  <span className="rm-inv-cat-tag">
                    {CAT_ICONS[r.invItem.category]} {CAT_LABELS[r.invItem.category] || r.invItem.category}
                  </span>
                  <span className="rm-inv-stock-tag">
                    {fmtQty(totalStock(r.invItem))}{r.invItem.baseUnit} in stock
                  </span>
                  {r.invItem.unitPrice > 0 && (
                    <span className="rm-inv-price-tag">₹{r.invItem.unitPrice}/{r.invItem.unit}</span>
                  )}
                </div>
              )}
            </div>

            <div className="rm-ing-qty-wrap">
              <input
                className="rm-input rm-ing-qty"
                type="number" min={0} step="any"
                placeholder={bulkMode ? `for ${bulkQty}` : 'qty'}
                value={r.quantity}
                onChange={e => upd(r._tempId, 'quantity', e.target.value)}
              />
              {perItemHint(r)}
              {stockWarn(r)}
              {rowCost(r)}
              {unitMismatchWarn(r)}
            </div>

            <UnitToggle value={r.unit} onChange={v => upd(r._tempId, 'unit', v)} />

            <button className="rm-ing-del" onClick={() => remove(r._tempId)} title="Remove row">✕</button>
          </div>
        ))}

        <button className="rm-add-ing" onClick={add}><span>+</span> Add Ingredient</button>
      </div>

      {/* ── Stats + cost bar ── */}
      <div className="rm-cost-preview">
        <div className="rm-cost-preview-item">
          <span className="rm-cost-preview-label">Total ingredients</span>
          <span className="rm-cost-preview-val">{ingredients.filter(r => r.label).length}</span>
        </div>
        <div className="rm-cost-preview-item">
          <span className="rm-cost-preview-label">Linked to inventory</span>
          <span className="rm-cost-preview-val">{linkedCount} / {ingredients.filter(r => r.label).length}</span>
        </div>
        {totalCost > 0 && (
          <div className="rm-cost-preview-item rm-cost-preview-item--highlight">
            <span className="rm-cost-preview-label">Est. raw cost / item</span>
            <span className="rm-cost-preview-val">₹{totalCost.toFixed(2)}</span>
          </div>
        )}
        {/* FIX: Show both gross margin and net margin (after GST) when taxable */}
        {sellingPrice > 0 && totalCost > 0 && (
          <>
            <div className="rm-cost-preview-item rm-cost-preview-item--margin">
              <span className="rm-cost-preview-label">
                {isTaxable ? 'Gross margin (excl. GST)' : 'Est. gross margin'}
              </span>
              <span className="rm-cost-preview-val">{grossMarginPct}%</span>
            </div>
            {isTaxable && (
              <div className="rm-cost-preview-item">
                <span className="rm-cost-preview-label">GST deducted (5%)</span>
                <span className="rm-cost-preview-val">
                  ₹{(sellingPrice - revenuePerItem).toFixed(2)}/item
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="rm-editor-actions">
        <button className="rm-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="rm-btn-save" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? <span className="rm-spinner" /> : '✓'} {saving ? 'Saving…' : 'Save Recipe'}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DISH CARD
═══════════════════════════════════════════════════════════ */
const DishCard = ({ dish, mealType, categoryName, onEdit, summary }) => {
  const meta  = summary?.find(s => s.mealType === mealType && s.categoryName === categoryName && s.dishName === dish.name);
  const done  = meta?.hasRecipe ?? false;
  const count = meta?.ingredientCount ?? 0;
  return (
    <div className={`rm-dish-card${done ? ' rm-dish-card--done' : ''}`}
      onClick={() => onEdit(dish, mealType, categoryName)}>
      <div className="rm-dish-card-left">
        <span className="rm-dish-dot-sm" style={{ background: TYPE_COLORS[dish.type] }} />
        <div>
          <p className="rm-dish-name">{dish.name}</p>
          <p className="rm-dish-sub">{dish.medium?.price ? `₹${dish.medium.price}` : 'No price'}{dish.isTaxable ? ' · GST' : ''}</p>
        </div>
      </div>
      <div className="rm-dish-card-right">
        {done
          ? <span className="rm-recipe-done">✓ {count} ing.</span>
          : <span className="rm-recipe-missing">Add recipe</span>}
        <span className="rm-dish-edit-arrow">›</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const RecipeManager = ({ cafeid }) => {
  const [menu,       setMenu]       = useState([]);
  const [inventory,  setInventory]  = useState([]);
  const [summary,    setSummary]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [invLoad,    setInvLoad]    = useState(true);
  const [completion, setCompletion] = useState({ total: 0, done: 0, pct: 0 });
  const [activeMeal, setActiveMeal] = useState(null);
  const [activeCat,  setActiveCat]  = useState(null);
  const [editing,    setEditing]    = useState(null);
  const [toast,      setToast]      = useState(null);
  const [search,     setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [menuRes, sumRes] = await Promise.all([
        fetch(`${host}/order/fetchMenu?cafeid=${cafeid}`, { headers: { Authorization: getToken() } }),
        fetch(`${host}/recipe/recipeSummary`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body:    JSON.stringify({ cafeid }),
        }),
      ]);
      const menuJson = await menuRes.json();
      const sumJson  = await sumRes.json();
      const menuArr  = Array.isArray(menuJson?.menu) ? menuJson.menu : [];
      setMenu(menuArr);
      if (menuArr.length) { setActiveMeal(menuArr[0].type); setActiveCat(menuArr[0].categories?.[0]?.name || null); }
      if (sumJson.success) { setSummary(sumJson.summary || []); setCompletion({ total: sumJson.total, done: sumJson.withRecipe, pct: sumJson.completion }); }
    } catch (e) { console.error('RecipeManager load:', e); }
    finally { setLoading(false); }
  }, [cafeid]);

  const loadInventory = useCallback(async () => {
    setInvLoad(true);
    try {
      const res  = await fetch(`${host}/api/inventory/${cafeid}`, { headers: { Authorization: getToken() } });
      const json = await res.json();
      const arr  = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      setInventory(arr.filter(i => i.isActive !== false));
    } catch (e) { console.error('Inventory load:', e); }
    finally { setInvLoad(false); }
  }, [cafeid]);

  useEffect(() => { load(); loadInventory(); }, [load, loadInventory]);

  const activeMealObj  = menu.find(m => m.type === activeMeal);
  const activeCatObj   = activeMealObj?.categories?.find(c => c.name === activeCat);
  const filteredDishes = (activeCatObj?.dishes || []).filter(d =>
    !search || d.name.toLowerCase().includes(search.toLowerCase()));

  const allDishes = menu.flatMap(m =>
    m.categories.flatMap(c => c.dishes.map(d => ({ dish: d, mealType: m.type, categoryName: c.name })))
  ).filter(({ dish }) => search && dish.name.toLowerCase().includes(search.toLowerCase()));

  const lowStockItems = inventory.filter(i => i.lowStockThreshold > 0 && i.quantity <= i.lowStockThreshold);

  const openEditor  = (dish, mt, cat) => setEditing({ dish, mealType: mt, categoryName: cat });
  const closeEditor = () => setEditing(null);
  const handleSaved = (msg) => { setToast({ msg, type: 'ok' }); load(); closeEditor(); };

  if (loading) return (
    <div className="rm-loading"><div className="rm-loading-spinner" /><p>Loading menu…</p></div>
  );

  if (editing) return (
    <div className="rm-root">
      <div className="rm-overlay-backdrop" onClick={closeEditor} />
      <div className="rm-overlay-panel">
        <RecipeEditor
          dish={editing.dish} mealType={editing.mealType} categoryName={editing.categoryName}
          cafeid={cafeid} inventory={inventory} onSaved={handleSaved} onClose={closeEditor}
        />
      </div>
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );

  return (
    <div className="rm-root">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <header className="rm-header">
        <div className="rm-header-left">
          <h1 className="rm-header-title">Recipe<br/>Manager</h1>
          <p className="rm-header-sub">
            Link dish ingredients to live inventory.<br/>
            Track stock usage and raw material costs.
          </p>
        </div>
        <div className="rm-header-right">
          <div className="rm-completion-card">
            <Ring pct={completion.pct} size={64} stroke={6} />
            <div className="rm-completion-text">
              <span className="rm-completion-pct">{completion.pct}%</span>
              <span className="rm-completion-sub">{completion.done}/{completion.total} recipes</span>
            </div>
          </div>
          <div className="rm-inv-snapshot">
            <span className="rm-inv-snapshot-num">{invLoad ? '…' : inventory.length}</span>
            <span className="rm-inv-snapshot-lbl">inventory items</span>
            {lowStockItems.length > 0 && (
              <span className="rm-inv-snapshot-low">⚠ {lowStockItems.length} low</span>
            )}
          </div>
        </div>
      </header>

      {lowStockItems.length > 0 && (
        <div className="rm-low-stock-banner">
          <span>⚠</span>
          <span>
            <strong>Low stock:</strong>{' '}
            {lowStockItems.slice(0, 4).map(i => i.name).join(', ')}
            {lowStockItems.length > 4 && ` +${lowStockItems.length - 4} more`}
          </span>
        </div>
      )}

      <div className="rm-search-wrap">
        <span className="rm-search-icon">⌕</span>
        <input className="rm-search" placeholder="Search any dish…" value={search}
          onChange={e => setSearch(e.target.value)} />
        {search && <button className="rm-search-clear" onClick={() => setSearch('')}>✕</button>}
      </div>

      {search.length > 0 ? (
        <div className="rm-search-results">
          {allDishes.length === 0
            ? <p className="rm-empty">No dishes match "{search}"</p>
            : allDishes.map(({ dish, mealType, categoryName }) => (
                <DishCard key={`${mealType}|${categoryName}|${dish.name}`}
                  dish={dish} mealType={mealType} categoryName={categoryName}
                  onEdit={openEditor} summary={summary} />
              ))
          }
        </div>
      ) : (
        <div className="rm-body">
          <div className="rm-meal-tabs">
            {menu.map(m => {
              const ms = summary.filter(s => s.mealType === m.type);
              const md = ms.filter(s => s.hasRecipe).length;
              return (
                <button key={m.type}
                  className={`rm-meal-tab${activeMeal === m.type ? ' active' : ''}`}
                  onClick={() => { setActiveMeal(m.type); setActiveCat(m.categories?.[0]?.name || null); }}>
                  <span>{MEAL_ICONS[m.type] || '🍽️'}</span>
                  <span className="rm-meal-name">{m.type}</span>
                  <span className={`rm-meal-badge${md === ms.length && ms.length > 0 ? ' done' : ''}`}>{md}/{ms.length}</span>
                </button>
              );
            })}
          </div>

          <div className="rm-content">
            <nav className="rm-cat-nav">
              {(activeMealObj?.categories || []).map(cat => {
                const cs = summary.filter(s => s.mealType === activeMeal && s.categoryName === cat.name);
                const cd = cs.filter(s => s.hasRecipe).length;
                return (
                  <button key={cat.name}
                    className={`rm-cat-btn${activeCat === cat.name ? ' active' : ''}`}
                    onClick={() => setActiveCat(cat.name)}>
                    <span className="rm-cat-name">{cat.name}</span>
                    <span className={`rm-cat-count${cd === cs.length && cs.length > 0 ? ' done' : ''}`}>{cd}/{cs.length}</span>
                  </button>
                );
              })}
            </nav>

            <div className="rm-dish-list">
              {filteredDishes.length === 0
                ? <p className="rm-empty">No dishes in this category.</p>
                : filteredDishes.map(dish => (
                    <DishCard key={dish.name} dish={dish}
                      mealType={activeMeal} categoryName={activeCat}
                      onEdit={openEditor} summary={summary} />
                  ))
              }
            </div>
          </div>

          <div className="rm-footer-tip">
            <span>💡</span>
            <p>
              Ingredients linked to inventory show live stock levels and cost estimates.
              Unlinked custom items can still be saved — link them later when you add them to inventory.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeManager;